import { useEffect, useState } from 'react';

export const usePerformanceOptimization = () => {
  const [isLowPerformance, setIsLowPerformance] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    // Mobile device detection
    const detectMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      const isSmallScreen = window.innerWidth <= 768;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      setIsMobile(isMobileDevice || (isSmallScreen && isTouchDevice));
    };

    detectMobile();
    window.addEventListener('resize', detectMobile);

    // Enhanced performance detection
    const detectPerformance = () => {
      let performanceScore = 0;

      // Check device memory (if available)
      if ('deviceMemory' in navigator) {
        if (navigator.deviceMemory < 2) performanceScore += 3;
        else if (navigator.deviceMemory < 4) performanceScore += 2;
        else if (navigator.deviceMemory < 8) performanceScore += 1;
      }

      // Check hardware concurrency
      if ('hardwareConcurrency' in navigator) {
        if (navigator.hardwareConcurrency < 2) performanceScore += 3;
        else if (navigator.hardwareConcurrency < 4) performanceScore += 2;
        else if (navigator.hardwareConcurrency < 8) performanceScore += 1;
      }

      // Check connection speed (if available)
      if ('connection' in navigator) {
        const connection = navigator.connection;
        if (connection.effectiveType === 'slow-2g') performanceScore += 4;
        else if (connection.effectiveType === '2g') performanceScore += 3;
        else if (connection.effectiveType === '3g') performanceScore += 2;
        else if (connection.effectiveType === '4g' && connection.downlink < 1.5) performanceScore += 1;
      }

      // Mobile device penalty
      if (isMobile) performanceScore += 1;

      // Battery level check (if available)
      if ('getBattery' in navigator) {
        navigator.getBattery().then((battery) => {
          if (battery.level < 0.2) performanceScore += 1;
          if (performanceScore >= 3) setIsLowPerformance(true);
        });
      } else if (performanceScore >= 3) {
        setIsLowPerformance(true);
      }

      // Frame rate test
      let frameCount = 0;
      let startTime = performance.now();
      
      const testFrame = () => {
        frameCount++;
        if (frameCount < 60) {
          requestAnimationFrame(testFrame);
        } else {
          const endTime = performance.now();
          const fps = 60000 / (endTime - startTime);
          if (fps < 30) {
            setIsLowPerformance(true);
          }
        }
      };

      requestAnimationFrame(testFrame);
    };

    // Run performance detection after a short delay
    const timer = setTimeout(detectPerformance, 1000);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('resize', detectMobile);
      clearTimeout(timer);
    };
  }, []);

  const getOptimizedProps = (baseProps = {}) => {
    const shouldReduce = isLowPerformance || prefersReducedMotion || isMobile;

    if (shouldReduce) {
      return {
        ...baseProps,
        animate: prefersReducedMotion ? false : baseProps.animate,
        transition: prefersReducedMotion
          ? { duration: 0.01 }
          : {
              ...baseProps.transition,
              duration: Math.min((baseProps.transition?.duration || 0.3) * 0.5, 0.2)
            },
        className: `${baseProps.className || ''} ${prefersReducedMotion ? 'reduced-motion' : ''}`,
      };
    }
    return baseProps;
  };

  return {
    isLowPerformance,
    prefersReducedMotion,
    isMobile,
    shouldReduceAnimations: isLowPerformance || prefersReducedMotion || isMobile,
    getOptimizedProps,
  };
};

export default usePerformanceOptimization;
